import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '@shape/feature-flag';
import { ColumnDef, CellContext, EllipsisTextWithTooltip, Loading } from '@shape-ds/components';
import { t } from 'i18next';
import { severityMap, AgregNumber } from 'components';
import { SeverityPills, buildColumnsObject } from '@shape/lighthouse-components';
import AssetBreadCrumbTooltip from 'components/AssetBreadCrumbTooltip/AssetBreadCrumbTooltip';
import { EventItem } from 'queries/events/types';
import { dateTime, formatDurationString, getDuration, sortDatesDesc } from 'utils/DateUtil';
import { SimilarEventLink } from '../components/EventDetails/components/EventFooter/SimilarEventList/SimilarEventLink';
import EventStatus from '../components/EventDetails/components/EventStatus/EventStatus';

interface useColumnsProps {
  splitScreen: boolean;
  isClosedStatusTab: boolean;
  isAllStatusTab: boolean;
}
type SortFn = (a: { original: EventItem }, b: { original: EventItem }) => number;
export const sortDuration: SortFn = (a: { original: EventItem }, b: { original: EventItem }) => {
  const durationA = getDuration(a.original.alarm_datetime_start, a.original.alarm_datetime_end);
  const durationB = getDuration(b.original.alarm_datetime_start, b.original.alarm_datetime_end);

  const diffDurations = durationA.asMinutes() - durationB.asMinutes();

  if (diffDurations > 0) {
    return 1;
  }
  if (diffDurations < 0) {
    return -1;
  }
  return 0;
};

export type EventItemColumns = {
  [key in keyof EventItem & { agreg: string; see_event: string }]: Partial<
    ColumnDef<EventItem & { agreg: string; see_event: string }>
  >;
};

export const eventsColumns: EventItemColumns = {
  severity: {
    size: 70,
    cell: (row: CellContext<EventItem & { agreg: string }, unknown>) => {
      const severityLevel = row.row.original.severity;

      return <SeverityPills severity={severityMap[severityLevel]} />;
    },
  },
  asset: {
    size: 111,
    sortingFn: (a: { original: EventItem }, b: { original: EventItem }) => {
      return a.original.asset.name.localeCompare(b.original.asset.name);
    },
    cell: (row: CellContext<EventItem & { agreg: string }, unknown>) =>
      row.row.parentId ? null : <AssetBreadCrumbTooltip assetName={row.row.original.asset.name} />,
  },
  description: {
    enableSorting: false,
    cell: (row: CellContext<EventItem & { agreg: string }, unknown>) => {
      const { description } = row.row.original;
      if (description === 'placeholder') {
        return <Loading size="15px" />;
      }

      return <EllipsisTextWithTooltip text={description} />;
    },
    size: 111,
  },
  alarm_datetime_start: {
    size: 120,

    sortingFn: (a: { original: EventItem }, b: { original: EventItem }) =>
      sortDatesDesc(a.original.alarm_datetime_start, b.original.alarm_datetime_start),
    cell: (row: CellContext<EventItem & { agreg: string; start_date: string }, unknown>) => {
      return dateTime(row.row.original.alarm_datetime_start ?? row.row.original.start_date);
    },
  },
  duration: {
    size: 90,
    sortingFn: sortDuration,
    cell: (row: CellContext<EventItem & { agreg: string }, unknown>) => {
      if (row.row.original.duration) return row.row.original.duration;
      const duration = formatDurationString(
        row.row.original.alarm_datetime_start,
        row.row.original.alarm_datetime_end,
      );

      return duration;
    },
  },
  classification: {
    size: 90,
    enableSorting: false,
    cell: (row: CellContext<EventItem, unknown>) => {
      if (!row.row.original.classification) {
        return '-';
      }
      return t(
        `event_page:status_count_panel.status.classification.${row.row.original.classification}`,
      );
    },
  },

  closed_by: {
    enableSorting: false,
    size: 120,
    cell: (row: CellContext<EventItem & { agreg: string }, unknown>) => {
      return (
        <div style={{ maxWidth: '120px' }}>
          <EllipsisTextWithTooltip text={row.row.original.closed_by || ''} />
        </div>
      );
    },
  },

  status: {
    size: 115,
    cell: (row: CellContext<EventItem, unknown>) => (
      <EventStatus status={row.row.original.status} />
    ),
  },

  agreg: {
    cell: (row: CellContext<EventItem, unknown>) => (
      <AgregNumber value={row.row.original.children?.length} />
    ),
    size: 42,
  },
  see_event: {
    enableSorting: false,
    header: '',
    size: 32,
    cell: (row: CellContext<EventItem & { see_event: string }, unknown>) => {
      return SimilarEventLink(row.row.original as EventItem);
    },
  },
};
export const useEventColumns = ({
  isClosedStatusTab,
  isAllStatusTab,
  splitScreen,
}: useColumnsProps) => {
  const { t } = useTranslation();
  const isRecordsEnabled = useFeatureFlag('records');
  const overrideColumns = Object.keys(eventsColumns).filter((key) => {
    if (key === 'closed_by') {
      return isClosedStatusTab;
    }
    if (key === 'status') {
      return isAllStatusTab;
    }
    if (key === 'classification') {
      return isAllStatusTab || isClosedStatusTab;
    }
    if (key === 'records' && (splitScreen || !isRecordsEnabled)) {
      return false;
    }
    if (key === 'see_event') return false;
    return true;
  });
  return {
    columns: buildColumnsObject<EventItem & { agreg?: string; see_event?: string }>({
      columns: eventsColumns,
      getLabel: (key) => t(`event_page:table.${key}`, { defaultValue: '' }),
      overrideColumns,
    }),
  };
};
